import { FC, useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import strings from '../admin.translations';
import { COLUMN_DEFINITIONS, ColumnKey, DEFAULT_VISIBLE } from './AdminContractTable.config';

interface ColumnSelectorProps {
  visible: ColumnKey[];
  onChange: (cols: ColumnKey[]) => void;
}

const AdminContractColumnSelector: FC<ColumnSelectorProps> = ({ visible, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const visibleSet = new Set(visible);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (key: ColumnKey) => {
    const next = visibleSet.has(key)
      ? visible.filter(k => k !== key)
      : COLUMN_DEFINITIONS.filter(c => visibleSet.has(c.key) || c.key === key).map(c => c.key);
    onChange(next);
  };

  const reset = () => onChange(DEFAULT_VISIBLE);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 rounded border border-primary-std text-sm px-3 py-1.5 font-medium text-secondary-std transition-colors hover:bg-secondary-std"
      >
        {strings.contractsSection.table.showColumns}
        <FiChevronDown className={`h-6 w-6 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded border border-primary-std bg-primary-std shadow-lg">
          <div className="flex items-center justify-between border-b border-primary-std px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-secondary-std">Columns</span>
            <button
              type="button"
              onClick={reset}
              className="text-xs text-secondary-std underline hover:text-primary-std"
            >
              {strings.common.reset}
            </button>
          </div>

          <ul className="max-h-64 overflow-y-auto">
            {COLUMN_DEFINITIONS.map(col => (
              <li key={col.key}>
                <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2.5 hover:bg-secondary-std">
                  <input
                    type="checkbox"
                    checked={visibleSet.has(col.key)}
                    onChange={() => toggle(col.key)}
                    className="h-3.5 w-3.5 rounded"
                  />
                  <span className="select-none text-xs text-primary-std">
                    {strings.contractsSection.table[col.key]}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdminContractColumnSelector;
